/** Query/API trend granularity (lowercase in URLs). */
export enum FinanceTrendGranularity {
  Daily = "daily",
  Monthly = "monthly",
}

/** Mirrors Prisma `FinanceActivityStatus`. */
export enum FinanceActivityStatus {
  Active = "ACTIVE",
  Completed = "COMPLETED",
  Cancelled = "CANCELLED",
}

/** Mirrors Prisma `ActivityParticipantRole`. */
export enum ActivityParticipantRole {
  Primary = "PRIMARY",
  Secondary = "SECONDARY",
  Responsible = "RESPONSIBLE",
  Participant = "PARTICIPANT",
  Beneficiary = "BENEFICIARY",
  VisitTarget = "VISIT_TARGET",
}

/** Mirrors Prisma `FinanceTransactionType`. */
export enum FinanceTransactionType {
  Income = "INCOME",
  Expense = "EXPENSE",
  /** System-generated outgoing leg of a completed transfer request (source context debit). */
  TransferOut = "TRANSFER_OUT",
  /** System-generated incoming leg of a completed internal transfer (destination context credit). */
  TransferIn = "TRANSFER_IN",
  /**
   * Internal account-to-account transfer: ONE balanced journal entry moving money between two of a branch's own
   * asset accounts. A first-class type with its own endpoint (`POST /finance/transfers`) — deliberately NOT in
   * {@link FinanceManualTransactionType} (the generic income/expense create flow). Never counts toward income,
   * expense, or surplus/deficit; only the asset composition changes.
   */
  Transfer = "TRANSFER",
}

/** Subset for manual parish/core create flows (INCOME | EXPENSE only). */
export enum FinanceManualTransactionType {
  Income = "INCOME",
  Expense = "EXPENSE",
}

/** Mirrors Prisma `FinancePaymentMethod`. */
export enum FinancePaymentMethod {
  Cash = "CASH",
  BankTransfer = "BANK_TRANSFER",
  MobileMoney = "MOBILE_MONEY",
  Card = "CARD",
  Check = "CHECK",
  Other = "OTHER",
}

/** Mirrors Prisma `PledgeStatus`. */
export enum PledgeStatus {
  /** Awaiting admin review — a pledge captured by a public Form Purpose, not yet accepted. Approval → OPEN. */
  Pending = "PENDING",
  Open = "OPEN",
  PartiallyFulfilled = "PARTIALLY_FULFILLED",
  Fulfilled = "FULFILLED",
  Cancelled = "CANCELLED",
  Overdue = "OVERDUE",
}

/** Mirrors Prisma `PledgeContributorType`. */
export enum PledgeContributorType {
  Member = "MEMBER",
  Group = "GROUP",
  Ministry = "MINISTRY",
  /** Person who is not (yet) a member, group, or ministry — captured by free-text name + contact. */
  External = "EXTERNAL",
}

/**
 * Mirrors Prisma `PledgeKind`. A pledge is either a financial
 * commitment (`Money`) or an in-kind commitment — goods OR services
 * (`InKind`, e.g. "50 chairs", "20 sacks of rice"). When the kind
 * is `InKind`, `inKindDescription` carries what's being pledged
 * and `amountPledged` carries the unit count.
 */
export enum PledgeKind {
  Money = "MONEY",
  InKind = "IN_KIND",
}

/** Mirrors Prisma `FinanceTransactionStatus`. */
export enum FinanceTransactionStatus {
  Active = "ACTIVE",
  Voided = "VOIDED",
  Cancelled = "CANCELLED",
}

/** Mirrors Prisma `FinanceTransactionKind`. */
export enum FinanceTransactionKind {
  Normal = "NORMAL",
  Reversal = "REVERSAL",
}

/** Receipt PDF/HTML template keys. */
export enum ReceiptTemplateKey {
  Classic = "classic",
  Compact = "compact",
  Formal = "formal",
}
